import { z } from 'zod';
import { appointmentLifecycleSchema, chatAttachmentMimeTypes, MAX_CHAT_ATTACHMENT_BYTES, MAX_PATIENT_SEARCH_RESULTS, paymentMethodSchema, roleSchema, taskPrioritySchema, taskStatusSchema, } from './constants.js';
import { digitsOnly, isValidBrazilianPhone, isValidCep, isValidCpf } from './normalization.js';
const nonEmptyId = z
    .string()
    .trim()
    .min(1)
    .max(128)
    .regex(/^[A-Za-z0-9_-]+$/);
const optionalPhone = z
    .string()
    .trim()
    .refine((value) => value === '' || isValidBrazilianPhone(value), {
    message: 'Informe um telefone válido com DDD.',
});
export const actorClaimsSchema = z.object({
    uid: nonEmptyId,
    clinicId: nonEmptyId,
    role: roleSchema,
    active: z.literal(true),
    authzVersion: z.number().int().nonnegative(),
});
export const patientInputSchema = z.object({
    fullName: z.string().trim().min(3, 'Informe o nome completo.').max(160),
    birthDate: z.string().date('Informe uma data de nascimento válida.'),
    cpf: z.string().trim().refine(isValidCpf, 'Informe um CPF válido.'),
    primaryPhone: z
        .string()
        .trim()
        .refine(isValidBrazilianPhone, 'Informe um telefone principal válido com DDD.'),
    secondaryPhone: optionalPhone.optional().default(''),
    emergencyPhone1: z
        .string()
        .trim()
        .refine(isValidBrazilianPhone, 'Informe o telefone de urgência com DDD.'),
    emergencyPhone2: optionalPhone.optional().default(''),
    email: z.email('Informe um e-mail válido.').max(254),
    cep: z.string().trim().refine(isValidCep, 'Informe um CEP válido.'),
    street: z.string().trim().min(2, 'Informe o logradouro.').max(160),
    number: z.string().trim().min(1, 'Informe o número.').max(20),
    complement: z.string().trim().max(80).optional().default(''),
    neighborhood: z.string().trim().min(2, 'Informe o bairro.').max(100),
    city: z.string().trim().min(2, 'Informe a cidade.').max(100),
    state: z.string().trim().toUpperCase().length(2, 'Informe a UF com duas letras.'),
    observations: z.string().trim().max(100_000).optional().default(''),
    socialCareEnabled: z.boolean().default(false),
    socialPrices: z
        .array(z.object({ appointmentTypeId: nonEmptyId, priceCents: z.number().int().nonnegative() }))
        .max(50)
        .default([]),
});
export const patientSearchInputSchema = z.object({
    query: z.string().trim().min(2).max(120),
    offset: z.number().int().min(0).max(250).default(0),
    limit: z.number().int().min(1).max(MAX_PATIENT_SEARCH_RESULTS).default(12),
    activeOnly: z.boolean().default(false),
});
export const patientInactiveReasonSchema = z.enum(['medical_discharge', 'abandonment']);
export const patientStatusSchema = z.enum(['active', 'inactive']);
export const appointmentTypeInputSchema = z.object({
    name: z.string().trim().min(2).max(100),
    durationMinutes: z.number().int().min(5).max(720).multipleOf(5),
    defaultPriceCents: z.number().int().nonnegative(),
    calendarColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
    active: z.boolean(),
});
export const appointmentCreateInputSchema = z.object({
    patientId: nonEmptyId,
    providerId: nonEmptyId,
    appointmentTypeId: nonEmptyId,
    startAt: z.iso.datetime(),
    expectedPaymentAt: z.iso.datetime().nullable().optional(),
    idempotencyKey: z.uuid(),
});
export const scheduleBlockInputSchema = z.object({
    providerId: nonEmptyId,
    startAt: z.iso.datetime(),
    endAt: z.iso.datetime(),
    reason: z.string().trim().max(240).optional().default(''),
    recurrenceDates: z.array(z.string().date()).max(120).optional().default([]),
    idempotencyKey: z.uuid(),
});
export const lifecycleCommandSchema = z.object({
    appointmentId: nonEmptyId,
    nextStatus: appointmentLifecycleSchema,
    expectedPaymentAt: z.iso.datetime().nullable().optional(),
    refundAmountCents: z.number().int().positive().optional(),
    idempotencyKey: z.uuid(),
});
export const rescheduleCommandSchema = z.object({
    appointmentId: nonEmptyId,
    newStartAt: z.iso.datetime(),
    idempotencyKey: z.uuid(),
});
export const ledgerCommandSchema = z.object({
    appointmentId: nonEmptyId,
    entryType: z.enum(['payment', 'refund']),
    amountCents: z.number().int().positive(),
    occurredAt: z.iso.datetime(),
    method: paymentMethodSchema.optional(),
    idempotencyKey: z.uuid(),
});
export const taskInputSchema = z.object({
    title: z.string().trim().min(2).max(140),
    description: z.string().trim().max(2000).optional().default(''),
    assigneeId: nonEmptyId,
    priority: taskPrioritySchema.default('normal'),
    dueAt: z.iso.datetime().nullable().optional(),
    patientId: nonEmptyId.nullable().optional(),
    appointmentId: nonEmptyId.nullable().optional(),
    idempotencyKey: z.uuid(),
});
export const taskUpdateInputSchema = z.object({
    taskId: nonEmptyId,
    assigneeId: nonEmptyId.optional(),
    status: taskStatusSchema.optional(),
    priority: taskPrioritySchema.optional(),
    dueAt: z.iso.datetime().nullable().optional(),
    idempotencyKey: z.uuid(),
});
export const chatMessageInputSchema = z
    .object({
    conversationId: nonEmptyId,
    text: z.string().trim().max(4000).optional().default(''),
    attachmentId: nonEmptyId.optional(),
    reference: z.object({ type: z.enum(['patient', 'task']), id: nonEmptyId }).optional(),
    clientMessageId: z.uuid(),
})
    .refine((value) => value.text.length > 0 || value.attachmentId != null, {
    message: 'Escreva uma mensagem ou selecione um anexo.',
});
export const attachmentReservationInputSchema = z.object({
    conversationId: nonEmptyId,
    fileName: z.string().trim().min(1).max(180),
    contentType: z.enum(chatAttachmentMimeTypes),
    sizeBytes: z.number().int().positive().max(MAX_CHAT_ATTACHMENT_BYTES),
    idempotencyKey: z.uuid(),
});
export const emailConfirmationInputSchema = z.object({
    appointmentId: nonEmptyId,
    idempotencyKey: z.uuid(),
    resend: z.boolean().default(false),
});
export const bootstrapUserInputSchema = z.object({
    email: z.email(),
    displayName: z.string().trim().min(2).max(120),
    role: roleSchema,
    clinicId: nonEmptyId,
    temporaryPassword: z.string().min(12).max(128),
});
export function normalizedPatientSearchFields(input) {
    const phoneDigits = [
        digitsOnly(input.primaryPhone),
        digitsOnly(input.secondaryPhone),
        digitsOnly(input.emergencyPhone1),
        digitsOnly(input.emergencyPhone2),
    ].filter((value, index, values) => value.length > 0 && values.indexOf(value) === index);
    return {
        cpfDigits: digitsOnly(input.cpf),
        primaryPhoneDigits: digitsOnly(input.primaryPhone),
        secondaryPhoneDigits: digitsOnly(input.secondaryPhone),
        emergencyPhone1Digits: digitsOnly(input.emergencyPhone1),
        emergencyPhone2Digits: digitsOnly(input.emergencyPhone2),
        phoneDigits,
        cepDigits: digitsOnly(input.cep),
    };
}
//# sourceMappingURL=schemas.js.map